import { stripAllPua } from "@nijiurachan/pua-command";
import type { PollCreateInput } from "@/entities/post/types";

export const POLL_MIN_OPTIONS = 2;
export const POLL_MAX_OPTIONS = 5;
const POLL_OPTION_MAX_CODE_POINTS = 80;
export const POLL_MIN_DURATION_MINUTES = 10;
export const POLL_MAX_DURATION_MINUTES = 1_440;
const DEFAULT_POLL_DURATION_MINUTES = "60";

export type PollEditorValue = {
  isEnabled: boolean;
  durationMinutes: string;
  options: string[];
};

export type PollEditorValidation = {
  durationError: string | null;
  optionErrors: Array<string | null>;
  payload: PollCreateInput | null;
};

export function createEmptyPollEditorValue(): PollEditorValue {
  return {
    isEnabled: false,
    durationMinutes: DEFAULT_POLL_DURATION_MINUTES,
    options: Array.from({ length: POLL_MIN_OPTIONS }, () => ""),
  };
}

export function normalizePollText(value: string): string {
  return stripAllPua(value).normalize("NFC").trim();
}

export function validatePollEditor(
  value: PollEditorValue,
): PollEditorValidation {
  if (!value.isEnabled) {
    return { durationError: null, optionErrors: [], payload: null };
  }

  const normalizedOptions = value.options.map(normalizePollText);
  const comparisonOptions = normalizedOptions;
  const optionErrors = normalizedOptions.map((option, index) => {
    if (option.length === 0) return "選択肢を入力してください。";
    if (Array.from(option).length > POLL_OPTION_MAX_CODE_POINTS) {
      return `選択肢は${POLL_OPTION_MAX_CODE_POINTS}文字以内で入力してください。`;
    }
    if (
      comparisonOptions.some(
        (comparison, otherIndex) =>
          otherIndex !== index && comparison === comparisonOptions[index],
      )
    ) {
      return "ほかの選択肢と重複しています。";
    }
    return null;
  });

  const durationMinutes = /^\d+$/u.test(value.durationMinutes)
    ? Number(value.durationMinutes)
    : Number.NaN;
  const durationError =
    Number.isInteger(durationMinutes) &&
    durationMinutes >= POLL_MIN_DURATION_MINUTES &&
    durationMinutes <= POLL_MAX_DURATION_MINUTES
      ? null
      : `アンケート受付期間は${POLL_MIN_DURATION_MINUTES}〜${POLL_MAX_DURATION_MINUTES}分の整数で入力してください。`;

  const hasValidOptionCount =
    normalizedOptions.length >= POLL_MIN_OPTIONS &&
    normalizedOptions.length <= POLL_MAX_OPTIONS;
  const payload =
    durationError === null &&
    hasValidOptionCount &&
    optionErrors.every((error) => error === null)
      ? { durationMinutes, options: normalizedOptions }
      : null;
  return { durationError, optionErrors, payload };
}
